// Mapa mundial. Equivalente a arc()+drawMap() del original
// (public/cabrillo/src/plantilla.html:95-106), incluida la leyenda de
// bandas (#ml) y el tooltip hit-test (PT), que en el original vive como
// variable global y acá queda como estado local del módulo — es estado
// puramente visual, nunca perteneció al motor. Ver src/cabrillo/CREDITOS.md.
//
// Misma proyección/colores/fórmulas que el original, sin cambios.

import { GEO } from '../datos.js';
import { bl } from '../geo.js';
import { BC, pad, css, observarRedimension } from './render-util.js';

function arc(a, b) {
    const r = Math.PI / 180;
    const v = (p) => [Math.cos(p[0] * r) * Math.cos(p[1] * r), Math.cos(p[0] * r) * Math.sin(p[1] * r), Math.sin(p[0] * r)];
    const A = v(a), B = v(b);
    const d = Math.acos(Math.max(-1, Math.min(1, A[0] * B[0] + A[1] * B[1] + A[2] * B[2])));
    if (d < 1e-4) return [a, b];
    const o = [];
    for (let i = 0; i <= 28; i++) {
        const t = i / 28;
        const s1 = Math.sin((1 - t) * d) / Math.sin(d);
        const s2 = Math.sin(t * d) / Math.sin(d);
        o.push([Math.asin(s1 * A[2] + s2 * B[2]) / r, Math.atan2(s1 * A[1] + s2 * B[1], s1 * A[0] + s2 * B[0]) / r]);
    }
    return o;
}

/**
 * @param {{mp: HTMLCanvasElement, tt: HTMLElement, md: HTMLSelectElement, ml: HTMLElement}} els
 * @param {ReturnType<import('../motor.js').crearAnalizador>} motor
 */
export function montar(els, motor) {
    const mc = els.mp.getContext('2d');
    let PT = [];

    const { bands } = motor.modelo;
    els.ml.innerHTML = bands.map((b, i) => `<span><i style="background:${BC(i)}"></i>${bl(b)}</span>`).join('');

    els.md.onchange = () => redraw();

    els.mp.onmousemove = (e) => {
        const b = els.mp.getBoundingClientRect();
        const x = e.clientX - b.left;
        const y = e.clientY - b.top;
        let h = null, d = 1e9;
        PT.forEach((p) => {
            const q = Math.hypot(p[0] - x, p[1] - y);
            if (q <= p[2] + 3 && q < d) {
                d = q;
                h = p;
            }
        });
        if (h) {
            els.tt.style.display = 'block';
            els.tt.style.left = Math.min(x + 12, b.width - 200) + 'px';
            els.tt.style.top = y + 12 + 'px';
            els.tt.textContent = h[3];
        } else {
            els.tt.style.display = 'none';
        }
    };
    els.mp.onmouseleave = () => {
        els.tt.style.display = 'none';
    };

    function redraw() {
        PT = [];
        const { D, PL, bands, t0, home } = motor.modelo;
        const { cur, bf } = motor.obtenerEstado();
        const nb = bands.length;
        const dpr = devicePixelRatio || 1;
        const W = els.mp.clientWidth;
        if (W === 0) return; // aún sin layout válido; el ResizeObserver vuelve a llamar cuando lo tenga
        const H = Math.round((W * 145) / 360);

        els.mp.style.height = H + 'px';
        els.mp.width = W * dpr;
        els.mp.height = H * dpr;
        mc.setTransform(dpr, 0, 0, dpr, 0, 0);

        const X = (lo) => ((lo + 180) / 360) * W;
        const Y = (la) => ((85 - la) / 145) * H;
        const line = css(els.mp, '--line');
        const fg = css(els.mp, '--fg');
        const mut = css(els.mp, '--mut');
        let fl = true;
        const path = () =>
            GEO.forEach((rg) => {
                mc.beginPath();
                rg.forEach((c, i) => (i ? mc.lineTo(X(c[0]), Y(c[1])) : mc.moveTo(X(c[0]), Y(c[1]))));
                mc.closePath();
                fl ? mc.fill() : mc.stroke();
            });

        mc.strokeStyle = line;
        mc.lineWidth = 0.5;
        for (let la = -60; la <= 80; la += 20) {
            mc.beginPath();
            mc.moveTo(0, Y(la));
            mc.lineTo(W, Y(la));
            mc.stroke();
        }

        mc.fillStyle = line;
        mc.globalAlpha = 0.55;
        path();
        mc.globalAlpha = 1;
        fl = false;
        mc.strokeStyle = mut;
        mc.lineWidth = 0.4;
        path();

        // Terminador día/noche a la hora del cursor.
        const ms = t0 + cur * 6e5 + 3e5;
        const dt = new Date(ms);
        const uh = dt.getUTCHours() + dt.getUTCMinutes() / 60;
        const N = Math.floor((ms - Date.UTC(dt.getUTCFullYear(), 0, 0)) / 864e5);
        const g = ((360 / 365) * (N - 81) * Math.PI) / 180;
        const eot = 9.87 * Math.sin(2 * g) - 7.53 * Math.cos(g) - 1.5 * Math.sin(g);
        const dl = -Math.asin(0.39779 * Math.cos(((0.98565 * (N + 10) + 1.914 * Math.sin((0.98565 * (N - 2) * Math.PI) / 180)) * Math.PI) / 180)) || 1e-6;
        const sl = -15 * (uh - 12) - eot / 4;
        const pole = dl < 0 ? 85 : -60;

        mc.beginPath();
        mc.moveTo(0, Y(pole));
        for (let x = 0; x <= W; x += 3) {
            const h2 = (((x / W) * 360 - 180 - sl) * Math.PI) / 180;
            mc.lineTo(x, Y(Math.max(-60, Math.min(85, (Math.atan(-Math.cos(h2) / Math.tan(dl)) * 180) / Math.PI))));
        }
        mc.lineTo(W, Y(pole));
        mc.closePath();
        mc.fillStyle = 'rgba(10,15,40,.22)';
        mc.fill();

        const md = els.md.value;
        const h0 = Math.floor(cur / 6) * 6;
        const lo = md == 'win' ? cur - 5 : md == 'hr' ? h0 : 0;
        const hi = md == 'hr' ? h0 + 5 : cur;
        const agg = {};
        const seen = {};
        let tot = 0;

        D.forEach((r) => {
            if (r[0] > hi || r[0] < lo || (bf >= 0 && r[1] != bf)) return;
            tot++;
            const k = r[5] * 16 + r[1];
            agg[k] = (agg[k] || 0) + 1;
        });

        if (home) {
            mc.lineWidth = 1;
            D.forEach((r) => {
                if (r[0] > hi || r[0] < (md == 'acc' ? cur - 5 : lo) || (bf >= 0 && r[1] != bf)) return;
                const k = r[5] * 16 + r[1];
                if (seen[k]) return;
                seen[k] = 1;
                const P = PL[r[5]];
                mc.strokeStyle = BC(r[1]);
                mc.globalAlpha = md == 'hr' ? 0.3 : 0.16 + 0.34 * (1 - (cur - r[0]) / 6);
                let pv = null;
                arc(home, [P[1], P[2]]).forEach((q) => {
                    if (pv && Math.abs(q[1] - pv[1]) > 180) {
                        mc.stroke();
                        pv = null;
                    }
                    if (!pv) {
                        mc.beginPath();
                        mc.moveTo(X(q[1]), Y(q[0]));
                    } else mc.lineTo(X(q[1]), Y(q[0]));
                    pv = q;
                });
                mc.stroke();
            });
            mc.globalAlpha = 1;
        }

        Object.keys(agg)
            .sort((a, b) => agg[b] - agg[a])
            .reverse()
            .forEach((k) => {
                const n = agg[k];
                const P = PL[k >> 4];
                const b = k & 15;
                const an = (2 * Math.PI * b) / nb;
                const o = bf < 0 && nb > 1 ? [Math.cos(an), Math.sin(an)] : [0, 0];
                const rad = Math.min(2.2 + Math.sqrt(n) * 1.3, 16);
                const px = X(P[2]) + o[0] * rad * 0.4;
                const py = Y(P[1]) + o[1] * rad * 0.4;
                PT.push([px, py, rad, P[0] + ' · ' + bl(bands[b]) + ': ' + n + ' QSO']);
                mc.beginPath();
                mc.arc(px, py, rad, 0, 7);
                mc.globalAlpha = 0.72;
                mc.fillStyle = BC(b);
                mc.fill();
                mc.globalAlpha = 1;
                mc.strokeStyle = fg;
                mc.lineWidth = 0.4;
                mc.stroke();
            });

        if (home) {
            mc.fillStyle = fg;
            mc.font = 'bold 16px system-ui';
            mc.textAlign = 'center';
            mc.textBaseline = 'middle';
            mc.fillText('★', X(home[1]), Y(home[0]));
        }

        mc.font = '11px system-ui';
        mc.textAlign = 'left';
        mc.textBaseline = 'bottom';
        mc.fillStyle = mut;
        mc.fillText(
            tot +
                ' QSO' +
                { acc: ' acumulados', win: ' en los últimos 60 min (= QSO/h)', hr: ' en la hora ' + pad(new Date(t0 + h0 * 6e5).getUTCHours()) + ':00 UTC' }[md] +
                (bf >= 0 ? ' · ' + bl(bands[bf]) : ''),
            6,
            H - 6
        );
    }

    redraw();
    const desuscribir = motor.subscribe(redraw);
    const dejarDeObservar = observarRedimension(els.mp, redraw);

    return {
        actualizar: redraw,
        destruir() {
            desuscribir();
            dejarDeObservar();
        },
    };
}
